import React, { useEffect } from "react"

import { Clock, Map, RotateCcw, Table2 } from "lucide-react"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAuth } from "@/contexts/AuthContext"

import MapView from "./monitoring/MapView"
import RouteAssignmentListView from "./monitoring/RouteAssignmentListView"
import TimelineView from "./monitoring/TimelineView"
import TurnaroundsView from "./monitoring/TurnaroundsView"

/** The open board lives in the path so links, bookmarks and refreshes land on the same board. */
export const MONITORING_TABS = ["map", "timeline", "list", "turnarounds"] as const

export type MonitoringTab = (typeof MONITORING_TABS)[number]

export const MONITORING_PATH = "/collections/monitoring"

/** Boards without `allBoards`: a partner watches its routes, it does not run the yard. */
const WATCHING_TABS: readonly MonitoringTab[] = ["map", "timeline"]

const isMonitoringTab = (value: string, tabs: readonly MonitoringTab[]): value is MonitoringTab =>
  (tabs as readonly string[]).includes(value)

/** Unknown segments resolve to null; the effect below rewrites them to the canonical path. */
const tabFromPath = (pathname: string, tabs: readonly MonitoringTab[]): MonitoringTab | null => {
  if (!pathname.startsWith(MONITORING_PATH)) return null
  const segment = pathname.slice(MONITORING_PATH.length).replace(/^\/+/, "").split("/")[0]
  return isMonitoringTab(segment, tabs) ? segment : null
}

const MonitoringContent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { can } = useAuth()
  const tabs = can.allBoards ? MONITORING_TABS : WATCHING_TABS

  const routedTab = tabFromPath(location.pathname, tabs)
  // Route Performance links carry the route on the query and name no tab; they mean the timeline.
  const hasDeepLink = searchParams.has("date") || searchParams.has("routeId")
  const activeSubTab: MonitoringTab = routedTab ?? (hasDeepLink ? "timeline" : "map")

  // Replace, not push: otherwise Back lands straight back on the bare path.
  useEffect(() => {
    if (routedTab) return
    navigate(
      { pathname: `${MONITORING_PATH}/${activeSubTab}`, search: location.search },
      { replace: true }
    )
  }, [routedTab, activeSubTab, location.search, navigate])

  // The query string rides along: it holds the timeline's date and selected route.
  const handleSubTabChange = (value: string) => {
    if (!isMonitoringTab(value, tabs)) return
    navigate({ pathname: `${MONITORING_PATH}/${value}`, search: location.search })
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Scrolls sideways: four triggers don't fit a 360px phone. */}
      <div className="bg-white border-b border-palette-jungleGreen200 px-4 lg:px-6 py-1.5 flex-shrink-0 overflow-x-auto">
        <Tabs value={activeSubTab} onValueChange={handleSubTabChange}>
          <TabsList className="bg-transparent h-8 p-0 gap-0.5 w-max">
            <TabsTrigger
              value="map"
              className="data-[state=active]:bg-palette-jungleGreen100 data-[state=active]:text-palette-jungleGreen900 text-palette-jungleGreen500 gap-1.5 px-3 py-1 rounded text-xs font-medium"
            >
              <Map size={12} />
              Map
            </TabsTrigger>
            <TabsTrigger
              value="timeline"
              className="data-[state=active]:bg-palette-jungleGreen100 data-[state=active]:text-palette-jungleGreen900 text-palette-jungleGreen500 gap-1.5 px-3 py-1 rounded text-xs font-medium"
            >
              <Clock size={12} />
              Timeline
            </TabsTrigger>
            {can.allBoards && (
              <>
                <TabsTrigger
                  value="list"
                  className="data-[state=active]:bg-palette-jungleGreen100 data-[state=active]:text-palette-jungleGreen900 text-palette-jungleGreen500 gap-1.5 px-3 py-1 rounded text-xs font-medium"
                >
                  <Table2 size={12} />
                  List
                </TabsTrigger>
                <TabsTrigger
                  value="turnarounds"
                  className="data-[state=active]:bg-palette-jungleGreen100 data-[state=active]:text-palette-jungleGreen900 text-palette-jungleGreen500 gap-1.5 px-3 py-1 rounded text-xs font-medium"
                >
                  <RotateCcw size={12} />
                  Turnarounds
                </TabsTrigger>
              </>
            )}
          </TabsList>
        </Tabs>
      </div>

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {activeSubTab === "map" && <MapView />}
        {activeSubTab === "timeline" && <TimelineView />}
        {activeSubTab === "list" && <RouteAssignmentListView />}
        {activeSubTab === "turnarounds" && <TurnaroundsView />}
      </div>
    </div>
  )
}

export default MonitoringContent
